import { useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { collection, onSnapshot, query, getDocs, orderBy } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { Collaborator } from '@/models';
import type { CollaboratorPrivate } from '@/features/Collaborators/Models/collaborator.types';
import { collaboratorsQueryKeys } from './useCollaboratorsQuery';
import { useSecureCollaborators } from '@/features/Collaborators/hooks/useSecureCollaborators';
import { useCollaboratorsWithSalary } from '@/features/Collaborators/hooks/useCollaboratorsWithSalary';
import { useCapabilityCatalogQuery } from './useCapabilityCatalogQuery';
import { resolveCollaboratorResources } from '@/features/Collaborators/utils/resourcePresentation';

// Mantém um único listener Firestore ativo enquanto houver componentes usando o hook
const snapshotManager: {
  unsub: (() => void) | null;
  listeners: number;
} = {
  unsub: null,
  listeners: 0,
};

interface RealtimeQueryResult {
  data: CollaboratorPrivate[];
  isLoading: boolean;
  error: Error | null;
}

export function useCollaboratorsRealtimeQuery(options?: {
  enabled?: boolean;
  withSalary?: boolean;
  withResources?: boolean;
}): RealtimeQueryResult {
  const { enabled = true, withSalary = true, withResources = true } = options || {};
  const queryClient = useQueryClient();
  const capabilityCatalogQuery = useCapabilityCatalogQuery({ enabled: enabled && withResources });

  // Primeira chamada carrega os dados uma única vez (getDocs-like)
  const queryResult = useQuery<CollaboratorPrivate[]>({
    initialData: () => (queryClient.getQueryData(collaboratorsQueryKeys.all) as CollaboratorPrivate[] | undefined) ?? [],
    queryKey: collaboratorsQueryKeys.all,
    queryFn: async () => {
      // Se já existe no cache, retorna direto para impedir loading
      const cached = queryClient.getQueryData(collaboratorsQueryKeys.all) as CollaboratorPrivate[] | undefined;
      if (cached && cached.length) return cached;

      const snap = await getDocs(query(collection(db, 'collaborators_private'), orderBy('name')));
      return snap.docs.map((doc) => ({ id: doc.id, ...doc.data() } as CollaboratorPrivate));
    },
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: enabled,
  });

  // Listener em tempo-real: mantém cache atualizado sem novos fetches
  useEffect(() => {
    if (!enabled) return;

    snapshotManager.listeners += 1;

    // Cria o listener se ainda não existir
    if (!snapshotManager.unsub) {
      console.log('[realtime] creating Firestore snapshot');
      const q = query(collection(db, 'collaborators_private'), orderBy('name'));
      snapshotManager.unsub = onSnapshot(q, (snap) => {
        const data = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() } as CollaboratorPrivate));
        console.log('[realtime] snapshot update', data.length);
        queryClient.setQueryData(collaboratorsQueryKeys.all, data);
      });
    }

    return () => {
      snapshotManager.listeners -= 1;
      if (snapshotManager.listeners === 0 && snapshotManager.unsub) {
        console.log('[realtime] disposing snapshot');
        snapshotManager.unsub();
        snapshotManager.unsub = null;
      }
    };
  }, [queryClient, enabled]);

  // SECURITY: Filter salary data based on user permissions (pre-migration protection)
  const secureData = useSecureCollaborators(queryResult.data || []);

  // POST-MIGRATION: Fetch salary data from subcollection and merge back
  const enrichedResult = useCollaboratorsWithSalary(secureData, withSalary);

  // Sem salário, ler direto o secureData tira um render inteiro do caminho crítico: o hook acima
  // guarda o resultado em useState, então só publica os dados no render seguinte ao snapshot.
  const baseData = withSalary ? enrichedResult.data : secureData;

  const resolvedData = useMemo(
    () => (withResources
      ? baseData.map((collaborator) => resolveCollaboratorResources(collaborator, capabilityCatalogQuery.data))
      : baseData),
    [baseData, capabilityCatalogQuery.data, withResources]
  );

  return {
    data: resolvedData,
    isLoading: queryResult.isLoading
      || (withSalary && enrichedResult.isLoading)
      || (withResources && capabilityCatalogQuery.isLoading),
    error: queryResult.error || enrichedResult.error,
  };
}
